import { useState } from "react";
import { type LocationRow } from "../lib/api";
import { MediaViewer } from "./MediaViewer";

/** Shared by LocationsPage (Tracks) and HobbyShopsPage -- a card with the
 *  address as a link out to Google Maps, an inline embedded map (no API key
 *  needed -- the plain maps.google.com "output=embed" form works for a
 *  simple address query), a photo gallery with the same click-to-popup
 *  lightbox as event media, and a mailto "Contact" link to the manager. */
export function LocationCard({ location }: { location: LocationRow }) {
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);

  return (
    <div className="card event-card">
      <h3>{location.name}</h3>
      {location.address && (
        <p className="muted">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {location.address}
          </a>
        </p>
      )}
      {location.description && <p>{location.description}</p>}
      {(location.phone || location.website) && (
        <p className="muted">
          {location.phone}
          {location.phone && location.website && " · "}
          {location.website && (
            <a href={location.website} target="_blank" rel="noopener noreferrer">{location.website}</a>
          )}
        </p>
      )}
      {location.hours && (
        <p className="muted" style={{ whiteSpace: "pre-line" }}>
          <strong>Hours</strong>
          {"\n"}
          {location.hours}
        </p>
      )}
      {location.managerEmail && (
        <p>
          <a href={`mailto:${location.managerEmail}?subject=${encodeURIComponent(`Question about ${location.name}`)}`}>
            Contact {location.name}
          </a>
        </p>
      )}
      {location.address && (
        <iframe
          className="location-map-embed"
          src={`https://www.google.com/maps?q=${encodeURIComponent(location.address)}&output=embed`}
          loading="lazy"
          title={`Map of ${location.name}`}
        />
      )}
      {location.photoUrls && location.photoUrls.length > 0 && (
        <div className="media-grid" style={{ marginTop: "0.75rem" }}>
          {location.photoUrls.map((url) => (
            <div className="media-item" key={url}>
              <img src={url} alt={location.name} onClick={() => setViewingPhoto(url)} />
            </div>
          ))}
        </div>
      )}
      {viewingPhoto && (
        <MediaViewer url={viewingPhoto} name={location.name} type="photo" onClose={() => setViewingPhoto(null)} />
      )}
    </div>
  );
}
